import {Box, SxProps, Theme} from '@mui/material';
import {useCan, usePersona} from 'src/permissions/hooks';
import {GridSortModel} from '@mui/x-data-grid';
import {ChangeEvent, FC, useEffect, useState} from 'react';
import {createSearchParams, useNavigate} from 'react-router-dom';
import {businessesApi, chargersApi, contractsApi, locationsApi, reOwnerApi, usersApi,} from 'src/api';
import {useAppSelector, useDefineIsRealEstateRole} from 'src/app/hooks';
import {store} from 'src/app/store';
import {BusinessDetails} from 'src/components/businesses/BusinessDetails';
import {ConnectorStatusStrip} from 'src/components/chargers/ConnectorStatusStrip';
import {BusinessSignature} from 'src/components/businesses/BusinessSignature';
import {BusinessTables} from 'src/components/businesses/BusinessTables';
import {BusinessTerms} from 'src/components/businesses/BusinessTerms';
import {ChargerSortFieldEnum, ContractSortFieldEnum, LocationSortFieldEnum} from 'src/enums';
import {preloaderChangeStatus} from 'src/stores/common/commonSlice';
import {BusinessDTO, ChargePointSearchRequestData, TableParamsType} from 'src/types';
import {isErrorObject} from 'src/utils/helper';
import {NotifyError} from 'src/utils/notification';
import {BusinessTermsRfid} from '../../components/businesses/BusinessTermsRfid';
import {ReOwnerGetRealEstateRequest} from 'src/types/reOwner';
import {UserTypeEnum} from 'src/enums/common';

const pageSx: SxProps<Theme> = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mt: 2.5,
};

/** The three text panels share the card the page's other blocks have. */
const panelSx: SxProps<Theme> = {
    background: '#fff',
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    p: 3,
};

/** The terms and the signature, level with each other. */
const termsRowSx: SxProps<Theme> = {
    display: 'grid',
    gridTemplateColumns: {xs: '1fr', lg: '7fr 5fr'},
    gap: 2,
    alignItems: 'stretch',
};

type BusinessByIdQueryTrigger = ReturnType<typeof businessesApi.useLazyGetBusinessByIdQuery>[0];
type RealEstateByIdQueryTrigger = ReturnType<typeof reOwnerApi.useLazyGetRealEstateQuery>[0];

const handleGetBusinessInfo =
    (trigger: BusinessByIdQueryTrigger | RealEstateByIdQueryTrigger) =>
        async (dataObject: ReOwnerGetRealEstateRequest) => {
            store.dispatch(preloaderChangeStatus(true));
            try {
                return await trigger({...dataObject}).unwrap();
            } catch (error) {
                if (isErrorObject(error)) {
                    NotifyError(error.data.message);
                }
            } finally {
                store.dispatch(preloaderChangeStatus(false));
            }
        };

export const BusinessInfo: FC = () => {
    const isRealEstate = useDefineIsRealEstateRole();
    const canReadBusiness = useCan('businesses.read');
    const canReadTerms = useCan('businessTerms.read');
    const persona = usePersona();

    const {activeBusiness} = useAppSelector((state) => state.common);
    const {authInfo} = useAppSelector((state) => state.common);
    const realEstateId = useAppSelector((state) => state.common.authInfo?.realEstate?.id);
    const [getBusinessTrigger, businessResponse] = businessesApi.useLazyGetBusinessByIdQuery();
    const [getRealEstateTrigger, realEstateResponse] = reOwnerApi.useLazyGetRealEstateQuery();
    const trigger = isRealEstate ? getRealEstateTrigger : getBusinessTrigger;
    const response = isRealEstate ? realEstateResponse : businessResponse;

    const [getLocations, {data: locations}] = locationsApi.useLazyGetLocationsQuery();
    const [getChargers, {data: chargers}] = chargersApi.useLazyGetChargersQuery();
    const [getContracts, {data: contracts}] = contractsApi.useLazyGetContractsQuery();
    const [getUsers, {data: users}] = usersApi.useLazyGetUsersQuery();

    const navigate = useNavigate();

    // Only the tenant: no filters to mirror here, and the server forces the id for anyone whose
    // reach is narrower than the business they are looking at.
    const {data: statusCounts} = chargersApi.useGetConnectorStatusCountsQuery(
        {businessId: response.data?.id},
        {skip: !response.data?.id}
    );

    const [locationsSortValue, setLocationsSortValue] = useState({
        sortAsc: true,
        sortField: '',
    });
    const [chargersSortValue, setChargersSortValue] = useState({
        sortAsc: true,
        sortField: '',
    });
    const [contractsSortValue, setContractsSortValue] = useState({
        sortAsc: true,
        sortField: '',
    });

    const defaultTableSize = 100;

    const [activeTab, setActiveTab] = useState<number>(0);
    const [tableParams, setTableParams] = useState<TableParamsType>({
        page: 0,
        size: defaultTableSize,
    });

    const handleGetLocations = async (
        businessId: number,
        companyId: number | undefined,
        params: TableParamsType
    ) => {
        const data = {
            businessId,
            companyId,
            sortAsc: locationsSortValue.sortAsc,
            sortField: locationsSortValue.sortField || LocationSortFieldEnum.ID,
        };
        await getLocations({data, params}).unwrap();
    };
    const handleGetChargers = async (businessId: number, params: TableParamsType) => {
        const data: ChargePointSearchRequestData = {
            businessId,
            sortAsc: chargersSortValue.sortAsc,
            sortField: chargersSortValue.sortField || ChargerSortFieldEnum.CREATED_AT,
        };

        // Company comes from the caller's reach, not from their account record.

        await getChargers({data, params}).unwrap();
    };
    const handleGetContracts = async (businessId: number, params: TableParamsType) => {
        const data = {
            businessId,
            sortAsc: contractsSortValue.sortAsc,
            sortField: contractsSortValue.sortField || ContractSortFieldEnum.CREATED,
        };
        await getContracts({data, params}).unwrap();
    };
    const handleGetUsers = async (businessId: number, params: TableParamsType) => {
        const isCompanyUser = persona.isCompany;
        const userTypes = isCompanyUser
            ? [UserTypeEnum.EMPLOYEE]
            : [UserTypeEnum.PRIVATE_DRIVER, UserTypeEnum.EMPLOYEE, UserTypeEnum.COMPANY];

        const data = {
            businessId,
            userTypes,
        };
        await getUsers({data, params}).unwrap();
    };

    useEffect(() => {
        if (response.data) {
            void handleGetLocations(response.data.id, authInfo?.company?.id, tableParams);
        }
    }, [response, locationsSortValue]);

    useEffect(() => {
        if (response.data) {
            void handleGetChargers(response.data.id, tableParams);
        }
    }, [response, chargersSortValue]);

    useEffect(() => {
        if (response.data) {
            void handleGetContracts(response.data.id, tableParams);
        }
    }, [response, contractsSortValue]);

    useEffect(() => {
        if (response.data) {
            void handleGetUsers(response.data.id, tableParams);
        }
    }, [response]);

    // Each detail page asks about its own entity; `singlePages` was one subject for all of
    // them and, being absent from the catalog, sent every one of them straight to /404.

    useEffect(() => {

        const data = {
            businessId: activeBusiness,
            realEstateId,
        };
        void handleGetBusinessInfo(trigger)(data);
    }, []);

    const handleChangePage = (page: number) => {
        setTableParams({
            page,
            size: tableParams.size || defaultTableSize,
        });
    };

    const handleChangeRowsNumber = (e: ChangeEvent<HTMLInputElement>) => {
        setTableParams({
            page: 0,
            size: +e.target.value || defaultTableSize,
        });
    };

    const handleSetActiveTab = (value: number) => {
        setActiveTab(value);
        handleChangePage(0);
    };

    const handleSort = (value: GridSortModel) => {
        if (activeTab === 0) {
            setLocationsSortValue({
                sortAsc: value ? value[0].sort === 'asc' : true,
                sortField: (value && value[0].field) || LocationSortFieldEnum.ID,
            });
        }
        if (activeTab === 1) {
            setChargersSortValue({
                sortAsc: value ? value[0].sort === 'asc' : true,
                sortField: (value && value[0].field) || ChargerSortFieldEnum.CREATED_AT,
            });
        }
        if (activeTab === 2) {
            setContractsSortValue({
                sortAsc: value ? value[0].sort === 'asc' : true,
                sortField: (value && value[0].field) || ContractSortFieldEnum.CREATED,
            });
        }
    };

    useEffect(() => {
        if (activeTab === 0) {
            response.data && void handleGetLocations(response.data.id, authInfo?.company?.id, tableParams);
        }
        if (activeTab === 1) {
            response.data && void handleGetChargers(response.data.id, tableParams);
        }
        if (activeTab === 2) {
            response.data && void handleGetContracts(response.data.id, tableParams);
        }
    }, [tableParams]);

    return (
        <Box sx={pageSx}>
            {response.data && (
                <>
                    {canReadBusiness && (
                        // The business and its four totals in one card. The totals were a block of
                        // their own below the form, all four wearing the same brand mark and none of
                        // them leading anywhere, though the tabs below list exactly those rows.
                        <BusinessDetails
                            data={response.data as BusinessDTO}
                            locations={locations?.page?.totalElements ?? 0}
                            chargers={chargers?.page?.totalElements ?? 0}
                            contracts={contracts?.page?.totalElements ?? 0}
                            users={users?.page?.totalElements ?? 0}
                        />
                    )}

                    <ConnectorStatusStrip
                        data={statusCounts}
                        actionLabel="seeChargers"
                        // In the query string rather than in `location.state`: the chargers list
                        // keeps its filter there now, so the handed-over status survives a reload and
                        // a Back - and the link can be pasted to somebody.
                        onSelect={(status) => navigate({
                            pathname: '/app/chargers',
                            search: createSearchParams({connectorStatus: status}).toString(),
                        })}
                        onSelectChargePointStatus={(status) => navigate({
                            pathname: '/app/chargers',
                            search: createSearchParams({chargePointStatus: status}).toString(),
                        })}
                    />

                    <BusinessTables
                        handleSort={handleSort}
                        locations={locations?.content ?? []}
                        chargers={chargers?.content ?? []}
                        contracts={contracts?.content ?? []}
                        locationsTotalPage={locations?.page?.totalPages ?? 0}
                        chargersTotalPage={chargers?.page?.totalPages ?? 0}
                        contractsTotalPage={contracts?.page?.totalPages ?? 0}
                        handleChangePage={handleChangePage}
                        handleChangeRowsNumber={handleChangeRowsNumber}
                        handleSetActiveTab={handleSetActiveTab}
                        size={tableParams.size ?? defaultTableSize}
                        tab={activeTab}
                        page={tableParams.page ?? 0}
                    />

                    {canReadTerms && (
                        // Two cards side by side rather than a seven-one-four grid whose middle
                        // column held nothing but a vertical rule. The RFID terms are the second tab
                        // of the terms card, not a fourth block below it.
                        <>
                            <Box sx={termsRowSx}>
                                <Box sx={panelSx}>
                                    <BusinessTerms data={response.data as BusinessDTO}/>
                                </Box>
                                <Box sx={panelSx}>
                                    <BusinessSignature data={response.data as BusinessDTO}/>
                                </Box>
                            </Box>
                            {/* Still its own card: it has its own editor, and folding two editors
                                into one tab strip is a change to how they are saved, not to how they
                                look. */}
                            <Box sx={panelSx}>
                                <BusinessTermsRfid data={response.data as BusinessDTO}/>
                            </Box>
                        </>
                    )}
                </>
            )}
        </Box>
    );
};
